import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { fetchProfilePicture } from './userProfilePicturesThunks';
// Cache expiry in ms (e.g., 24h)
const CACHE_TTL = 24 * 60 * 60 * 1000;

export const useBulkProfilePictures = (userIds = []) => {
  const dispatch = useDispatch();
  const {
    profilePictures = {},
    fetchedUsers = {},
    fetchedAt = {},
  } = useSelector((state) => state.profilePictures || {});

  // Debounced fetch, only re-run when fetchedUsers changes (drop profilePictures to avoid loop)
  const debouncedFetch = useCallback(
    debounce((ids) => {
      const uniqueIds = [...new Set(ids)];
      const now = Date.now();

      uniqueIds.forEach((userId) => {
        const isExpired =
          fetchedAt[userId] && now - fetchedAt[userId] > CACHE_TTL;

        if (
          userId &&
          (!fetchedUsers[userId] || isExpired) &&
          profilePictures[userId] === undefined
        ) {
          dispatch(fetchProfilePicture(userId));
        }
      });
    }, 200),
    [dispatch, fetchedUsers, fetchedAt]
  );

  useEffect(() => {
    if (userIds.length > 0) {
      debouncedFetch(userIds);
    }
    return () => {
      debouncedFetch.cancel();
    };
  }, [userIds, debouncedFetch]);

  // Return avatar states
  return userIds.reduce((acc, userId) => {
    if (profilePictures[userId] === undefined) {
      acc[userId] = { status: 'loading', url: null };
    } else if (profilePictures[userId] === null) {
      acc[userId] = { status: 'empty', url: null };
    } else {
      acc[userId] = { status: 'ready', url: profilePictures[userId] };
    }
    return acc;
  }, {});
};

// Single-user hook
export const useProfilePicture = (userId) => {
  const dispatch = useDispatch();
  const {
    profilePictures = {},
    fetchedUsers = {},
    fetchedAt = {},
  } = useSelector((state) => state.profilePictures || {});

  useEffect(() => {
    const now = Date.now();
    const isExpired =
      fetchedAt[userId] && now - fetchedAt[userId] > CACHE_TTL;

    if (
      userId &&
      (!fetchedUsers[userId] || isExpired) &&
      profilePictures[userId] === undefined
    ) {
      dispatch(fetchProfilePicture(userId));
    }
  }, [userId, dispatch, fetchedUsers, profilePictures, fetchedAt]);

  if (profilePictures[userId] === undefined) {
    return { status: 'loading', url: null };
  }
  if (profilePictures[userId] === null) {
    return { status: 'empty', url: null };
  }
  return { status: 'ready', url: profilePictures[userId] };
};
